import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const ContactFooter = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || email.length === 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5llken72m0gvhnvdel0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-white py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="bg-black rounded-3xl p-12 lg:p-20">
          {/* Logo Section */}
          <div className="flex justify-center mb-12">
            <img
              src="https://static.readdy.ai/image/439cae8384efc9e7b28c054739e211d1/7857bf78d2c9072a724b80513f3fbc5c.png"
              alt="EVE Smart Logo"
              title="EVE Smart Agricultural Services - إيف سمارت للخدمات الزراعية"
              className="h-20 w-auto"
            />
          </div>

          {/* Upper Section - Three Columns */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            {/* Newsletter - Right for Arabic (Larger) */}
            <div className={`md:col-span-5 ${isRTL ? 'md:order-1 text-right' : 'md:order-1 text-left'}`}>
              <h3 className="font-almarai font-light text-4xl md:text-5xl text-white mb-4">
                {t('footer.newsletter.title')}
              </h3>
              <p className="font-tajawal text-lg text-gray-400 leading-relaxed mb-8">
                {t('footer.newsletter.subtitle')}
              </p>
              
              <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form">
                <div className="mb-6">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.newsletter.placeholder')}
                    required
                    className="w-full bg-transparent border-b-2 border-white/30 text-white placeholder-gray-500 py-3 font-tajawal text-lg focus:outline-none focus:border-white transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-white text-black px-8 py-4 rounded-full font-tajawal text-base hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {isRTL ? 'جاري الإرسال...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      {t('footer.newsletter.button')}
                      <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
                    </>
                  )}
                </button>
                {submitStatus === 'success' && (
                  <p className="text-green-400 font-tajawal text-sm mt-4">
                    {isRTL ? 'تم الاشتراك بنجاح!' : 'Successfully subscribed!'}
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 font-tajawal text-sm mt-4">
                    {isRTL ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.'}
                  </p>
                )}
              </form>
            </div>

            {/* Quick Links - Middle */}
            <div className={`md:col-span-3 ${isRTL ? 'md:order-2 text-right' : 'md:order-2 text-left'}`}>
              <h4 className="font-tajawal text-sm uppercase text-gray-500 mb-6 tracking-wider">
                {t('footer.quickLinks.title')}
              </h4>
              <nav className="space-y-4" aria-label="Quick Links">
                <button
                  onClick={() => scrollToSection('home')}
                  className="block font-tajawal text-white hover:underline cursor-pointer"
                >
                  {t('footer.quickLinks.home')}
                </button>
                <button
                  onClick={() => scrollToSection('services')}
                  className="block font-tajawal text-white hover:underline cursor-pointer"
                >
                  <strong>{t('footer.quickLinks.services')}</strong>
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="block font-tajawal text-white hover:underline cursor-pointer"
                >
                  {t('footer.quickLinks.projects')}
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block font-tajawal text-white hover:underline cursor-pointer"
                >
                  {t('footer.quickLinks.contact')}
                </button>
              </nav>
            </div>

            {/* Contact - Left for Arabic */}
            <div className={`md:col-span-4 ${isRTL ? 'md:order-3 text-right' : 'md:order-3 text-left'}`}>
              <h4 className="font-tajawal text-sm uppercase text-gray-500 mb-6 tracking-wider">
                {t('footer.contact.title')}
              </h4>
              <div className="space-y-4 mb-8">
                <a
                  href="mailto:evevsmart@gmail.com"
                  className="block font-tajawal text-white hover:text-primary transition-colors"
                  aria-label="Email address"
                >
                  {t('footer.contact.email')}
                </a>
              </div>
              <h4 className="font-tajawal text-sm uppercase text-gray-500 mb-4 tracking-wider">
                {t('footer.contact.locationTitle')}
              </h4>
              <address className="font-tajawal text-white leading-relaxed not-italic">
                <strong>{t('footer.contact.location1')}</strong>
                <br />
                {t('footer.contact.location2')}
              </address>
            </div>
          </div>

          {/* Lower Bar */}
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              {/* Social Links - Right for Arabic */}
              <div className={`flex items-center gap-6 ${isRTL ? 'md:order-3' : 'md:order-1'}`}>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="font-tajawal text-white hover:underline cursor-pointer"
                  aria-label="LinkedIn"
                >
                  LinkedIn
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="font-tajawal text-white hover:underline cursor-pointer"
                  aria-label="Instagram"
                >
                  Instagram
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="font-tajawal text-white hover:underline cursor-pointer"
                  aria-label="Twitter"
                >
                  Twitter
                </a>
              </div>

              {/* Copyright - Center */}
              <div className="md:order-2">
                <p className="font-tajawal text-gray-400 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('footer.copyright')}
                </p>
              </div>

              {/* Legal Links - Left for Arabic */}
              <div className={`flex items-center gap-4 ${isRTL ? 'md:order-1' : 'md:order-3'}`}>
                <a 
                  href="/privacy" 
                  className="font-tajawal text-white hover:underline cursor-pointer"
                >
                  {t('footer.privacy')}
                </a>
                <a 
                  href="/terms" 
                  className="font-tajawal text-white hover:underline cursor-pointer"
                >
                  {t('footer.terms')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ContactFooter;
